import config from '@app/config';

import { Request } from '../../common';
import {
    type ApiResult,
    type BaseParams,
    type DiscordAuthorizationUrlResponse,
    type DiscordTokenRequestParams,
    type LoginParams,
    type MagicLinkParams,
    type MagicLinkResponseValue,
    type PasswordResetEmailParams,
    type PasswordResetEmailResponseValue,
    type PasswordResetParams,
    type PostParams,
    type RefreshParams,
    type Token,
    type TwitterAuthorizationUrlResponse,
    type TwitterTokenRequestParams,
    type TwoFactorApiResp,
    type TwoFactorConfirmParams,
    type TwoFactorDisableParams,
    type TwoFactorEnableResp,
    type TwoFactorLoginParams,
    type TwoFactorParams,
    type TwoFactorSendSMSResp,
} from '../../common/structs';
import { links } from '../../links';

const ignoreHeaders = config?.ignoreSegmentHeaders?.split(',') || [];

export async function confirm2FA(
    params: BaseParams & PostParams<TwoFactorConfirmParams>,
): Promise<ApiResult<TwoFactorApiResp>> {
    return Request.postRequest<TwoFactorApiResp>({
        ...params,
        target: links.user().twoFactorAuth().confirm(),
        settings: {
            ...params.settings,
        },
    });
}

export async function login2FA(params: BaseParams & PostParams<TwoFactorLoginParams>): Promise<ApiResult<Token>> {
    return Request.postRequest<Token>({
        ...params,
        target: links.auth().login2FA(),
        settings: {
            ...params.settings,
        },
    });
}

export async function disable2FA(
    params: BaseParams & PostParams<TwoFactorDisableParams>,
): Promise<ApiResult<TwoFactorApiResp>> {
    return Request.postRequest<TwoFactorApiResp>({
        ...params,
        target: links.user().twoFactorAuth().disable(),
        settings: {
            ...params.settings,
        },
    });
}

export async function sendSMS(params: BaseParams): Promise<ApiResult<TwoFactorSendSMSResp>> {
    return Request.postRequest<TwoFactorSendSMSResp>({
        ...params,
        target: links.user().twoFactorAuth().sendSms(),
        settings: {
            ...params.settings,
        },
        value: {},
    });
}

export async function enable2FA(
    params: BaseParams & PostParams<TwoFactorParams>,
): Promise<ApiResult<TwoFactorEnableResp>> {
    return Request.postRequest<TwoFactorEnableResp>({
        ...params,
        target: links.user().twoFactorAuth().enable(),
        settings: {
            ...params.settings,
        },
    });
}

export async function login(params: BaseParams & PostParams<LoginParams>): Promise<ApiResult<Token>> {
    return Request.postRequest<Token>({
        ...params,
        target: links.auth().login(),
        settings: {
            ...params.settings,
            credentials: 'omit',
            ignoreHeaders,
        },
    });
}

export async function getDiscordAuthorizationUrl(
    params: BaseParams & PostParams,
): Promise<ApiResult<DiscordAuthorizationUrlResponse>> {
    return Request.postRequest<DiscordAuthorizationUrlResponse>({
        ...params,
        target: links.auth().getDiscordAuthorizationUrl(),
        settings: {
            ...params.settings,
            credentials: 'omit',
            ignoreHeaders,
        },
    });
}

export async function discordLogin(params: BaseParams<DiscordTokenRequestParams>): Promise<ApiResult<Token>> {
    return Request.getRequest<Token>({
        ...params,
        target: links.auth().discordLogin(),
        settings: {
            ...params.settings,
            credentials: 'omit',
            ignoreHeaders,
        },
    });
}

export async function refresh(params: BaseParams & PostParams<RefreshParams>): Promise<ApiResult<Token>> {
    return Request.postRequest<Token>({
        ...params,
        target: links.auth().refresh(),
        settings: {
            ...params.settings,
            credentials: 'omit',
            ignoreHeaders,
        },
    });
}

export async function magicLink(
    params: BaseParams & PostParams<MagicLinkParams>,
): Promise<ApiResult<MagicLinkResponseValue>> {
    return Request.postRequest<MagicLinkResponseValue>({
        ...params,
        target: links.auth().magicLink(),
        settings: {
            ...params.settings,
            credentials: 'omit',
            ignoreHeaders,
        },
    });
}

export async function passwordResetEmail(
    params: BaseParams & PostParams<PasswordResetEmailParams>,
): Promise<ApiResult<PasswordResetEmailResponseValue>> {
    return Request.postRequest<PasswordResetEmailResponseValue>({
        ...params,
        target: links.auth().passwordReset(),
        settings: {
            ...params.settings,
            credentials: 'omit',
            ignoreHeaders,
        },
    });
}

export async function passwordReset(params: BaseParams & PostParams<PasswordResetParams>): Promise<ApiResult<Token>> {
    return Request.putRequest<Token>({
        ...params,
        target: links.auth().passwordReset(),
        settings: {
            ...params.settings,
            credentials: 'omit',
            ignoreHeaders,
        },
    });
}

export async function getTwitterAuthorizationUrl(
    params: BaseParams & PostParams,
): Promise<ApiResult<TwitterAuthorizationUrlResponse>> {
    return Request.postRequest<TwitterAuthorizationUrlResponse>({
        ...params,
        target: links.auth().getTwitterAuthorizationUrl(),
        settings: {
            ...params.settings,
            credentials: 'omit',
            ignoreHeaders,
        },
    });
}

async function twitterAuth(target: string, params: BaseParams<TwitterTokenRequestParams>): Promise<ApiResult<Token>> {
    return Request.getRequest<Token>({
        ...params,
        target,
        settings: {
            ...params.settings,
            credentials: 'omit',
            ignoreHeaders,
        },
    });
}

export async function twitterLogin(params: BaseParams<TwitterTokenRequestParams>): Promise<ApiResult<Token>> {
    return twitterAuth(links.auth().twitterLogin(), params);
}

export async function twitterToken(params: BaseParams<TwitterTokenRequestParams>): Promise<ApiResult<Token>> {
    return twitterAuth(links.auth().twitterToken(), params);
}

export async function createAccount(params: BaseParams & PostParams<LoginParams>): Promise<ApiResult<Token>> {
    return Request.postRequest<Token>({
        ...params,
        target: links.user().collection(),
        settings: {
            ...params.settings,
            credentials: 'omit',
            ignoreHeaders,
        },
    });
}
